import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

import { useDataApi } from '../../../context/DataApiContext'
import {
  consumeSuppressAutoApply,
  urlHasMeaningfulSearch,
} from '../../../utils/use-quick-filters'

// Route-level guard: before the page component mounts, check if the user has
// a default quick filter for this page and the URL is empty. If so, perform a
// soft history.replace into the default's URL so the page mounts once with
// the correct URL — no full reload, no flicker, no double data fetch.
export default function QuickFilterAutoApplyGuard({ pageType, children }) {
  const location = useLocation()
  const history = useHistory()
  const { dataProvider } = useDataApi()
  const [ready, setReady] = useState(false)
  // Bumped when the guard performs a redirect. Used as a key on the wrapping
  // Fragment so children unmount+remount once the URL has been replaced —
  // any useQueryState hooks inside re-initialise from the new URL instead of
  // fighting it with their stale at-mount state.
  const [redirectGen, setRedirectGen] = useState(0)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      // URL already carries user-applied filters → respect it.
      if (urlHasMeaningfulSearch(location.search)) {
        if (!cancelled) setReady(true)
        return
      }
      // User just clicked Réinitialiser → skip auto-apply once.
      if (consumeSuppressAutoApply(pageType)) {
        if (!cancelled) setReady(true)
        return
      }

      try {
        const result = await dataProvider.quickFilter.list(pageType)
        if (cancelled) return
        const all = [...(result.public || []), ...(result.private || [])]
        const def = result.defaultId
          ? all.find((f) => f.id === result.defaultId)
          : null
        if (def && def.urlQuery) {
          const desired = '?' + def.urlQuery
          if (desired !== window.location.search) {
            history.replace({ search: desired })
            if (!cancelled) setRedirectGen((g) => g + 1)
          }
        }
      } catch (e) {
        // Network or auth error — render children with whatever URL exists.
      } finally {
        if (!cancelled) setReady(true)
      }
    }

    check()
    return () => {
      cancelled = true
    }
    // Run exactly once per page mount.
    // eslint-disable-next-line
  }, [pageType])

  if (!ready) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }
  // Key forces a fresh mount of the page tree after the guard redirects, so
  // useQueryState inside reads the new URL on first render. Subsequent user
  // filter changes don't bump the key, so no state-loss remount on chip clicks.
  return <React.Fragment key={redirectGen}>{children}</React.Fragment>
}
